import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Info,
  XCircle,
  BarChart2,
  Trophy,
  Clock,
  HelpCircle,
  Zap,
  Hash,
  Brain,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  runTransaction,
  increment,
} from "firebase/firestore";

import { db } from "../../utils/firebase";
import { useAuth } from "../../hooks/AuthenticationProvider";

/*
  DataStream (DSST-Based)

  Based on the Digit Symbol Substitution Test (DSST) from the WAIS battery.
  Users see a digit, then must quickly identify the matching symbol from multiple choices (or vice versa).
  Measures both processing speed and accuracy. Helps build cognitive speed and mental efficiency
  often impacted by substance use.

  Color Theme: from-red-500 to-orange-600, text-red-300
*/

const DataStream = () => {
  const navigate = useNavigate();
  const { user, organizationId } = useAuth();

  // Game phases: intro, tutorial, play, results
  const [gameMode, setGameMode] = useState("intro");

  // Difficulty: easy, standard, advanced
  const [difficulty, setDifficulty] = useState("standard");

  // Score & Leaderboard
  const [score, setScore] = useState(0);
  const [highScores, setHighScores] = useState([]);

  // Feedback Overlay
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackCorrect, setFeedbackCorrect] = useState(false);

  // Instructions Modal
  const [showInstructions, setShowInstructions] = useState(false);

  // DSST-Specific State
  const [currentRound, setCurrentRound] = useState(0);
  const [totalRounds, setTotalRounds] = useState(20);
  const [gameCompleted, setGameCompleted] = useState(false);

  // For DSST, we typically have 9 digit-symbol mappings. We'll define them once.
  // You can replace these shapes/patterns or ASCII symbols with any you prefer.
  const digitSymbolMap = [
    { digit: 1, symbol: "★" },
    { digit: 2, symbol: "△" },
    { digit: 3, symbol: "☐" },
    { digit: 4, symbol: "◇" },
    { digit: 5, symbol: "☉" },
    { digit: 6, symbol: "☂" },
    { digit: 7, symbol: "♣" },
    { digit: 8, symbol: "♡" },
    { digit: 9, symbol: "♬" },
  ];

  // We'll show the user a digit and require them to pick from multiple symbols
  // or vice versa. For simplicity, let's always show a digit -> user picks symbol.

  const [currentDigit, setCurrentDigit] = useState(null);
  const [currentSymbolOptions, setCurrentSymbolOptions] = useState([]);
  const [correctResponses, setCorrectResponses] = useState(0);
  const [incorrectResponses, setIncorrectResponses] = useState(0);

  // Timer & gameplay management
  const [timeLeft, setTimeLeft] = useState(90); // default 90 seconds for standard
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef(null);
  const gameActive = useRef(false);

  // Timestamps to measure average response time
  const [responseStartTime, setResponseStartTime] = useState(null);
  const [responseTimes, setResponseTimes] = useState([]);
  const [averageResponseTime, setAverageResponseTime] = useState(0);

  // Start & end game timestamps
  const [gameStartTime, setGameStartTime] = useState(null);
  const [gameEndTime, setGameEndTime] = useState(null);

  // Tutorial
  const [tutorialStep, setTutorialStep] = useState(0);

  // Colors for DSST-based game (processing speed)
  const primaryGradient = "from-red-500 to-orange-600";
  const textAccent = "text-red-300";
  const primaryColorSolid = "bg-red-500";
  const highlightColor = "bg-gradient-to-r from-red-500 to-orange-600";

  // Difficulty settings
  // All can be tweaked to calibrate for your participants
  const difficultySettings = {
    easy: {
      timeLimit: 45,
      optionCount: 2, // fewer choices
      noFeedback: false,
    },
    standard: {
      timeLimit: 60,
      optionCount: 3,
      noFeedback: false,
    },
    advanced: {
      timeLimit: 90,
      optionCount: 4,
      noFeedback: true, // no immediate correctness feedback
    },
  };

  // Fetch high scores from Firebase
  useEffect(() => {
    const fetchHighScores = async () => {
      if (!organizationId) return;
      try {
        // This doc holds the leaderboards for DataStream
        const leaderboardDocRef = doc(
          db,
          `organizations/${organizationId}/gameScores`,
          "data-stream"
        );
        const docSnap = await getDoc(leaderboardDocRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const levelKey = difficulty.toString(); // 'easy' | 'standard' | 'advanced'
          const scores =
            data.leaderboards && data.leaderboards[levelKey]
              ? data.leaderboards[levelKey]
              : [];
          setHighScores(scores);
        } else {
          setHighScores([]);
        }
      } catch (error) {
        console.error("Error fetching high scores:", error);
      }
    };

    fetchHighScores();
  }, [organizationId, difficulty]);

  // Timer logic
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            endGame();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPlaying]);

  // Format time as M:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Start Game
  const startGame = () => {
    // Reset state for a new game
    setScore(0);
    setCurrentRound(0);
    setCorrectResponses(0);
    setIncorrectResponses(0);
    setResponseTimes([]);
    setAverageResponseTime(0);

    // Set timer
    setTimeLeft(difficultySettings[difficulty].timeLimit);
    setIsPlaying(true);
    gameActive.current = true;
    setGameStartTime(Date.now());
    setGameEndTime(null);

    // Start first round
    startNewRound();
  };

  // End Game
  const endGame = () => {
    setIsPlaying(false);
    gameActive.current = false;
    clearInterval(timerRef.current);
    setGameEndTime(Date.now());

    // Calculate final average response time
    if (responseTimes.length > 0) {
      const avg =
        responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
      setAverageResponseTime(Math.round(avg));
    }

    // Move to results
    setGameMode("results");
    setGameCompleted(currentRound >= totalRounds);
  };

  // Generate a new round: show a digit, create multiple symbols
  const startNewRound = () => {
    if (!gameActive.current || currentRound >= totalRounds) {
      endGame();
      return;
    }

    // Randomly pick a digit from 1-9
    const randomIndex = Math.floor(Math.random() * digitSymbolMap.length);
    const chosenMapping = digitSymbolMap[randomIndex];
    setCurrentDigit(chosenMapping.digit);

    // Options: must include the correct symbol + random distractors
    const correctSymbol = chosenMapping.symbol;

    let options = [correctSymbol];
    const { optionCount } = difficultySettings[difficulty];

    while (options.length < optionCount) {
      const randSymbol =
        digitSymbolMap[Math.floor(Math.random() * digitSymbolMap.length)]
          .symbol;
      if (!options.includes(randSymbol)) {
        options.push(randSymbol);
      }
    }
    // Shuffle options
    options.sort(() => Math.random() - 0.5);
    setCurrentSymbolOptions(options);

    setCurrentRound((prev) => prev + 1);

    // Start timing the user’s response
    setResponseStartTime(Date.now());
  };

  // Handle user selecting an option
  const handleOptionSelect = (chosenSymbol) => {
    if (!isPlaying) return;

    const responseTime = Date.now() - responseStartTime;
    setResponseTimes((prev) => [...prev, responseTime]);

    // Determine the correct symbol for the current digit
    const correctSymbol = digitSymbolMap.find(
      (m) => m.digit === currentDigit
    )?.symbol;

    const isCorrect = chosenSymbol === correctSymbol;

    if (isCorrect) {
      setCorrectResponses((prev) => prev + 1);
      // Score weighting: base + small speed bonus
      // This helps keep top scores around ~500 with strong performance
      let basePoints = 10; // each correct
      // Speed bonus: faster than 1 sec => +5, <2 sec => +3, <3 => +2, else +1
      let speedBonus = 1;
      if (responseTime < 1000) {
        speedBonus = 5;
      } else if (responseTime < 2000) {
        speedBonus = 3;
      } else if (responseTime < 3000) {
        speedBonus = 2;
      }

      const roundScore = basePoints + speedBonus;
      setScore((prev) => prev + roundScore);

      if (!difficultySettings[difficulty].noFeedback) {
        setFeedbackCorrect(true);
        setFeedbackMessage("Correct!");
        setShowFeedback(true);
        setTimeout(() => setShowFeedback(false), 600);
      }
    } else {
      setIncorrectResponses((prev) => prev + 1);
      // Small penalty
      setScore((prev) => (prev > 0 ? prev - 5 : 0));

      if (!difficultySettings[difficulty].noFeedback) {
        setFeedbackCorrect(false);
        setFeedbackMessage("Incorrect");
        setShowFeedback(true);
        setTimeout(() => setShowFeedback(false), 600);
      }
    }

    // Next round or end
    if (currentRound < totalRounds) {
      // Delay a bit to allow feedback
      const delay = difficultySettings[difficulty].noFeedback ? 300 : 600;
      setTimeout(() => {
        startNewRound();
      }, delay);
    } else {
      setTimeout(() => {
        endGame();
      }, 600);
    }
  };

  // Save score to Firebase
  const saveScore = async () => {
    if (!organizationId || !user?.email) return;

    try {
      // 🧠 Determine userName with fallbacks
      let userName = "Anonymous";
      if (user.firstName && user.lastName) {
        userName = `${user.firstName} ${user.lastName}`;
      } else if (user.displayName) {
        userName = user.displayName;
      } else if (user.email) {
        userName = user.email.split("@")[0];
      }

      // 🧱 Unified gameData object
      const gameData = {
        gameType: "data-stream",
        gameId: "data-stream",
        mode: "single",
        userId: user.email,
        userName,
        score,
        difficulty,
        correctResponses,
        incorrectResponses,
        averageResponseTime,
        gameLength: gameEndTime - gameStartTime,
        timestamp: new Date(),
      };

      // 🔗 References
      const leaderboardDocRef = doc(
        db,
        `organizations/${organizationId}/gameScores`,
        "data-stream"
      );
      const userDocRef = doc(
        db,
        `organizations/${organizationId}/users`,
        user.email
      );

      // ⚖️ Composite comparator: score > speed > accuracy
      const compositeComparator = (a, b) => {
        if (a.score !== b.score) return b.score - a.score;
        if (a.averageResponseTime !== b.averageResponseTime)
          return a.averageResponseTime - b.averageResponseTime;
        return (a.incorrectResponses || 0) - (b.incorrectResponses || 0);
      };

      // 🧾 Transaction for leaderboard + userScore
      await runTransaction(db, async (transaction) => {
        const leaderboardDoc = await transaction.get(leaderboardDocRef);
        let leaderboardData = leaderboardDoc.exists()
          ? leaderboardDoc.data()
          : { leaderboards: {} };

        const levelKey = difficulty.toString();
        let currentLeaderboard = leaderboardData.leaderboards[levelKey] || [];

        if (
          currentLeaderboard.length < 5 ||
          compositeComparator(gameData, currentLeaderboard[4]) < 0
        ) {
          currentLeaderboard.push(gameData);
          currentLeaderboard.sort(compositeComparator);
          currentLeaderboard = currentLeaderboard.slice(0, 5);
          leaderboardData.leaderboards[levelKey] = currentLeaderboard;
          transaction.set(leaderboardDocRef, leaderboardData, { merge: true });
        }

        transaction.update(userDocRef, {
          gameScore: increment(score),
        });
      });

      // 💾 Save to gameHistory
      const gameHistoryRef = doc(
        db,
        `organizations/${organizationId}/users/${
          user.email
        }/gameHistory/${Date.now()}`
      );
      await setDoc(gameHistoryRef, gameData);

      // 🔄 Reset UI
      setGameMode("intro");
      setScore(0);
    } catch (error) {
      console.error("Error saving score:", error);
    }
  };

  // Tutorial steps
  const advanceTutorial = () => {
    if (tutorialStep < 3) {
      setTutorialStep((prev) => prev + 1);
    } else {
      // Done with tutorial
      setGameMode("play");
      startGame();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-950 text-white p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/gameroom")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Gameroom</span>
          </button>

          <div className="flex items-center gap-3">
            <h1 className={`text-xl md:text-2xl font-bold ${textAccent}`}>
              Data Stream
            </h1>
            <div className="p-2 bg-red-500/10 rounded-full">
              <Zap className="text-red-400" size={20} />
            </div>
          </div>

          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="p-2 bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
          >
            <Info size={20} />
          </button>
        </div>

        {/* Instructions Modal */}
        <AnimatePresence>
          {showInstructions && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-slate-800 rounded-xl p-4 sm:p-6 max-w-2xl w-full border border-slate-700 max-h-[90vh] overflow-y-auto"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500/10 rounded-lg">
                      <Zap className="text-red-400" size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-white">
                      Data Stream – DSST
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowInstructions(false)}
                    className="p-2 bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                  >
                    <XCircle size={20} />
                  </button>
                </div>

                <div className="space-y-4 mb-6 text-slate-300">
                  <p>
                    <strong>Data Stream</strong> is based on the Digit Symbol
                    Substitution Test (DSST). Match each digit to its unique
                    symbol as quickly and accurately as possible. This exercise
                    hones processing speed and mental efficiency, essential for
                    recovery.
                  </p>
                  <p>
                    The faster and more accurate you are, the higher your score.
                    Watch out for mistakes—they'll cost you precious points.
                  </p>
                </div>

                <button
                  onClick={() => setShowInstructions(false)}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-600 text-white py-3 rounded-lg font-medium hover:brightness-110 transition-colors"
                >
                  Got it!
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Container */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl overflow-hidden">
          {/* Intro Screen */}
          {gameMode === "intro" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div
                  className={`w-20 h-20 ${highlightColor} rounded-full mx-auto flex items-center justify-center mb-4`}
                >
                  <Zap size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Data Stream
                </h2>
                <p className="text-slate-300 max-w-md mx-auto">
                  A fast-paced digit-symbol matching game designed to improve
                  processing speed in recovery. Train your brain to think
                  quickly and accurately!
                </p>
              </div>

              {/* Feature Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-red-500/10 rounded-full mb-2">
                    <Brain size={24} className="text-red-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    Processing Speed
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Strengthens your ability to rapidly process new information
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-red-500/10 rounded-full mb-2">
                    <Clock size={24} className="text-red-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">
                    1-2 minute sessions
                  </h3>
                  <p className="text-sm text-slate-300 text-center">
                    Designed to keep you quick and focused
                  </p>
                </div>

                <div className="bg-slate-700/50 rounded-lg p-4 flex flex-col items-center">
                  <div className="p-3 bg-red-500/10 rounded-full mb-2">
                    <Hash size={24} className="text-red-400" />
                  </div>
                  <h3 className="font-medium text-white mb-1">Symbol Coding</h3>
                  <p className="text-sm text-slate-300 text-center">
                    Match digits to symbols to improve mental agility
                  </p>
                </div>
              </div>

              {/* Difficulty selection & start buttons */}
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <div className="flex-1 max-w-xs">
                  <h3 className="font-medium text-white mb-2 text-center md:text-left">
                    Difficulty Level
                  </h3>
                  <div className="flex gap-2 justify-center md:justify-start">
                    {["easy", "standard", "advanced"].map((level) => (
                      <button
                        key={level}
                        onClick={() => setDifficulty(level)}
                        className={`flex-1 py-2 px-4 rounded-lg transition-colors capitalize ${
                          difficulty === level
                            ? `bg-gradient-to-r from-red-500 to-orange-600 text-white`
                            : `bg-slate-700 text-slate-300 hover:bg-slate-600`
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setGameMode("tutorial")}
                    className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <HelpCircle size={18} />
                    Tutorial
                  </button>

                  <button
                    onClick={() => {
                      setGameMode("play");
                      startGame();
                    }}
                    className="flex-1 py-3 px-6 bg-gradient-to-r from-red-500 to-orange-600 hover:brightness-110 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {/* Leaderboard */}
              {highScores.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Trophy size={18} className="text-yellow-400" />
                    High Scores
                  </h3>
                  <div className="bg-slate-700/30 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-slate-700/50">
                        <tr>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Rank
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Player
                          </th>
                          <th className="py-2 px-4 text-left text-slate-300">
                            Level
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Resp. Time
                          </th>
                          <th className="py-2 px-4 text-center text-slate-300">
                            Errors
                          </th>
                          <th className="py-2 px-4 text-right text-slate-300">
                            Score
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {highScores.map((hs, index) => {
                          let formattedName = hs.userName || "Anonymous";
                          if (formattedName.includes(" ")) {
                            const parts = formattedName.split(" ");
                            const firstName = parts[0];
                            const lastInitial = parts[1]
                              ? parts[1][0] + "."
                              : "";
                            formattedName = `${firstName} ${lastInitial}`;
                          }
                          return (
                            <tr
                              key={index}
                              className="border-t border-slate-700/50"
                            >
                              <td className="py-2 px-4 text-slate-300">
                                {index + 1}
                              </td>
                              <td className="py-2 px-4 text-white">
                                {formattedName}
                              </td>
                              <td className="py-2 px-4 text-slate-300 capitalize">
                                {hs.difficulty}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {hs.averageResponseTime
                                  ? `${hs.averageResponseTime} ms`
                                  : "N/A"}
                              </td>
                              <td className="py-2 px-4 text-center text-slate-300">
                                {hs.incorrectResponses ?? 0}
                              </td>
                              <td className="py-2 px-4 text-right text-red-300 font-medium">
                                {hs.score}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tutorial Screen */}
          {gameMode === "tutorial" && (
            <div className="p-6 md:p-8">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white mb-2">
                  Tutorial: Data Stream (DSST)
                </h2>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  {tutorialStep === 0 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        <strong>Data Stream</strong> is inspired by the classic
                        Digit Symbol Substitution Test. Each digit (1–9) is
                        paired with a unique symbol. Your goal is to match them
                        as quickly as possible.
                      </p>
                      <p className="text-slate-300">
                        This task targets <em>processing speed</em>, which can
                        be reduced by substance use. By practicing rapid
                        digit-symbol matching, you support neuroplasticity and
                        strengthen cognitive function during recovery.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 1 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        On each round, you'll see a digit (like{" "}
                        <span className="text-red-300">3</span>). You must pick
                        the correct symbol among the options. Example key:
                      </p>
                      <div className="bg-slate-800 p-3 rounded-lg mt-2">
                        <p className="text-slate-200 mb-1">
                          Digit-Symbol Mappings:
                        </p>
                        {digitSymbolMap.slice(0, 5).map((pair) => (
                          <span
                            key={pair.digit}
                            className="inline-block mr-4 text-red-300"
                          >
                            {pair.digit} → {pair.symbol}
                          </span>
                        ))}
                        <p className="text-slate-400 mt-2 text-sm">
                          (In the actual game, you’ll see all 1–9.)
                        </p>
                      </div>
                    </div>
                  )}

                  {tutorialStep === 2 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        **Scoring**: You get points for each correct match,
                        plus:
                      </p>
                      <ul className="list-disc list-inside text-slate-300 ml-4">
                        <li>Speed bonus for faster responses</li>
                        <li>A small penalty for wrong answers</li>
                      </ul>
                      <p className="text-slate-300">
                        In <strong>Advanced</strong> mode, you won't see
                        immediate feedback whether you're right or wrong. For
                        easier levels, you'll see quick feedback after each
                        choice.
                      </p>
                    </div>
                  )}

                  {tutorialStep === 3 && (
                    <div className="space-y-4">
                      <p className="text-slate-300">
                        **Tips**:
                        <ul className="list-disc list-inside ml-4 mt-1">
                          <li>
                            Glance at the digit, recall or quickly identify the
                            matching symbol
                          </li>
                          <li>Stay calm and focus on accuracy</li>
                          <li>Speed matters, but watch out for mistakes!</li>
                        </ul>
                      </p>
                      <p className="text-slate-300">
                        This game helps you develop faster cognitive processing,
                        which can aid everyday tasks in recovery (e.g., focusing
                        on group meetings, responding to triggers, etc.).
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between mt-8">
                {tutorialStep > 0 ? (
                  <button
                    onClick={() => setTutorialStep((prev) => prev - 1)}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Previous
                  </button>
                ) : (
                  <button
                    onClick={() => setGameMode("intro")}
                    className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    Back
                  </button>
                )}

                <button
                  onClick={advanceTutorial}
                  className={`py-2 px-4 bg-gradient-to-r from-red-500 to-orange-600 hover:brightness-110 text-white rounded-lg transition-colors`}
                >
                  {tutorialStep < 3 ? "Next" : "Start Game"}
                </button>
              </div>
            </div>
          )}

          {/* Gameplay Screen */}
          {gameMode === "play" && (
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="bg-red-500/20 px-4 py-2 rounded-lg">
                  <span className="text-red-300 font-medium capitalize">
                    Level: {difficulty}
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="bg-slate-700/50 px-4 py-2 rounded-lg flex items-center gap-2">
                    <Clock size={18} className="text-slate-400" />
                    <span className="text-white font-mono">
                      {formatTime(timeLeft)}
                    </span>
                  </div>

                  <div className="ml-4 bg-red-500/20 px-4 py-2 rounded-lg">
                    <span className="text-white font-medium">
                      Score: {score}
                    </span>
                  </div>
                </div>
              </div>

              {/* Main digit to match */}
              <div className="flex flex-col items-center mb-8">
                <h3 className="text-white font-medium mb-2">
                  Which symbol matches this digit?
                </h3>
                <div className="text-6xl font-bold text-red-300">
                  {currentDigit}
                </div>
              </div>

              {/* Options */}
              <div className="flex flex-wrap gap-4 justify-center max-w-2xl mx-auto mb-8">
                {currentSymbolOptions.map((symbol, i) => (
                  <button
                    key={i}
                    onClick={() => handleOptionSelect(symbol)}
                    className="bg-slate-800 w-20 h-20 rounded-lg flex items-center 
                 justify-center text-2xl transition-all transform 
                 hover:scale-105 focus:outline-none text-slate-200"
                    disabled={!isPlaying || showFeedback}
                  >
                    {symbol}
                  </button>
                ))}
              </div>

              {/* Feedback */}
              <AnimatePresence>
                {showFeedback && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm z-10"
                  >
                    <div className="text-center">
                      <div
                        className={`text-3xl font-bold ${
                          feedbackCorrect ? "text-green-400" : "text-red-400"
                        }`}
                      >
                        {feedbackMessage}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Stats */}
              <div className="mt-8 max-w-2xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Round</div>
                  <div className="text-white font-medium">
                    {currentRound}/{totalRounds}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Correct</div>
                  <div className="text-green-400 font-medium">
                    {correctResponses}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Errors</div>
                  <div className="text-red-400 font-medium">
                    {incorrectResponses}
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="text-slate-400 text-xs mb-1">Avg. Time</div>
                  <div className="text-white font-medium">
                    {responseTimes.length > 0
                      ? `${Math.round(
                          responseTimes.reduce((a, b) => a + b, 0) /
                            responseTimes.length
                        )} ms`
                      : "N/A"}
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center">
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        "Are you sure you want to end the game early?"
                      )
                    ) {
                      endGame();
                    }
                  }}
                  className="py-2 px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors text-sm"
                >
                  End Game
                </button>
              </div>
            </div>
          )}

          {/* Results Screen */}
          {gameMode === "results" && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-8">
                <div
                  className={`w-20 h-20 ${highlightColor} rounded-full mx-auto flex items-center justify-center mb-4`}
                >
                  <Trophy size={40} className="text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Game Complete!
                </h2>
                <p className="text-slate-300">
                  You've completed Data Stream on {difficulty} difficulty.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Trophy size={20} className="text-red-400" />
                    Your Results
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-2 border-b border-slate-700">
                      <span className="text-slate-300">Final Score</span>
                      <span className="text-2xl font-bold text-red-300">
                        {score}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Difficulty</span>
                      <span className="text-white font-medium capitalize">
                        {difficulty}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Correct</span>
                      <span className="text-green-400">{correctResponses}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Errors</span>
                      <span className="text-red-400">{incorrectResponses}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-300">Avg. Response Time</span>
                      <span className="text-white">
                        {averageResponseTime > 0
                          ? `${averageResponseTime} ms`
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-red-400" />
                    Performance Analysis
                  </h3>
                  <div className="space-y-5">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-slate-300 text-sm">Accuracy</span>
                        <span className="text-white text-sm">
                          {correctResponses + incorrectResponses > 0
                            ? `${Math.round(
                                (correctResponses /
                                  (correctResponses + incorrectResponses)) *
                                  100
                              )}%`
                            : "N/A"}
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-red-500 to-orange-600"
                          style={{
                            width:
                              correctResponses + incorrectResponses > 0
                                ? `${
                                    (correctResponses /
                                      (correctResponses + incorrectResponses)) *
                                    100
                                  }%`
                                : "0%",
                          }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-white font-medium mb-2">
                        Key Observations
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="text-slate-300">
                          <strong>High Accuracy:</strong> Indicates strong
                          short-term recall and quick visual processing.
                        </li>
                        <li className="text-slate-300">
                          <strong>Fast Response Times:</strong> Shows enhanced
                          cognitive speed and focus.
                        </li>
                        <li className="text-slate-300">
                          Misses or slow responses can improve with practice.
                          Keep training for faster mental agility!
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-white font-medium mb-2">
                        Cognitive Benefits
                      </h4>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-red-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-red-400" />
                          </div>
                          <span className="text-slate-300">
                            Reinforces neural pathways for processing speed
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-red-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-red-400" />
                          </div>
                          <span className="text-slate-300">
                            Helps maintain mental agility essential in
                            high-stakes or trigger situations
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <div className="p-1 bg-red-500/10 rounded-full mt-0.5">
                            <Brain size={14} className="text-red-400" />
                          </div>
                          <span className="text-slate-300">
                            Supplements overall cognitive rehabilitation in
                            recovery
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Buttons */}
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button
                  onClick={() => setGameMode("intro")}
                  className="py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                >
                  Back to Menu
                </button>

                {gameCompleted && (
                  <button
                    onClick={saveScore}
                    className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-600 hover:brightness-110 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    <Trophy size={18} />
                    Save Score
                  </button>
                )}

                <button
                  onClick={() => {
                    setGameMode("play");
                    startGame();
                  }}
                  className="py-3 px-6 bg-gradient-to-r from-red-500 to-orange-600 hover:brightness-110 text-white rounded-lg transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataStream;
